import * as React from "react";
import {
  useInput,
  useGetList,
  useList,
  ListContextProvider,
  ResourceContextProvider,
  Datagrid,
  TextField as RaTextField,
  DateField,
  FunctionField,
} from "react-admin";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import { useAuth } from "../../hooks/useAuth";

// #5 — an agency's per-carrier opt-out control, rendered as a React-Admin
// Datagrid. There are ~200 carriers, so we never list them all: the bound value
// is the array of opt-out entries { carrier, at, byName }. Pick a carrier from
// the search to turn it off (it stamps who/when and adds a row); each row shows
// the carrier, when it was turned off and by whom, and a button to turn it back
// on. Used for `disabledCarriers` (turned off for the agency's agents) and
// `blockedCarriers` (Applied GA blocks it for the agency).
const CarrierOptOutTable = (props) => {
  const {
    addLabel = "Add a carrier",
    actionLabel = "Turn on",
    dateLabel = "Turned off",
    helperText,
  } = props;
  const { field } = useInput(props);
  const entries = Array.isArray(field.value) ? field.value : [];
  const { user } = useAuth();
  const byName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const [input, setInput] = React.useState("");

  const { data: carriers = [], isLoading } = useGetList("carrier", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "value", order: "ASC" },
  });
  const byId = new Map(carriers.map((c) => [String(c.id), c]));
  const label = (c) => (c.productType ? `${c.value} (${c.productType})` : c.value);
  const has = (cid) => entries.some((e) => String(e.carrier) === String(cid));

  const rows = entries.map((e) => {
    const c = byId.get(String(e.carrier));
    return {
      id: String(e.carrier),
      carrier: e.carrier,
      carrierName: c ? label(c) : String(e.carrier),
      at: e.at,
      byName: e.byName || "",
    };
  });
  const options = carriers.filter((c) => !has(c.id));

  const add = (carrier) => {
    if (carrier) {
      field.onChange([
        ...entries,
        { carrier: carrier.id, at: new Date().toISOString(), byName },
      ]);
    }
    setInput("");
  };
  const remove = (cid) =>
    field.onChange(entries.filter((e) => String(e.carrier) !== String(cid)));

  const listContext = useList({ data: rows, isLoading });

  return (
    <Box data-testid={`carrier-optout-${props.source}`} sx={{ mt: 1 }}>
      {helperText && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {helperText}
        </Typography>
      )}
      <Autocomplete
        options={options}
        loading={isLoading}
        getOptionLabel={label}
        value={null}
        inputValue={input}
        onInputChange={(e, v) => setInput(v)}
        onChange={(e, c) => add(c)}
        blurOnSelect
        sx={{ maxWidth: 420, mb: 1 }}
        renderInput={(params) => (
          <TextField {...params} label={addLabel} size="small" />
        )}
      />
      {rows.length > 0 && (
        <ResourceContextProvider value="agency">
          <ListContextProvider value={listContext}>
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={{ maxWidth: 760 }}
            >
              <RaTextField source="carrierName" label="Carrier" sortable={false} />
              <DateField source="at" label={dateLabel} showTime sortable={false} />
              <RaTextField source="byName" label="By" sortable={false} />
              <FunctionField
                label=""
                render={(record) => (
                  <Button size="small" onClick={() => remove(record.carrier)}>
                    {actionLabel}
                  </Button>
                )}
              />
            </Datagrid>
          </ListContextProvider>
        </ResourceContextProvider>
      )}
    </Box>
  );
};

export default CarrierOptOutTable;
